/**
 * galaxy-paths.ts — v4M6.3b in-flight movement visualisation.
 *
 * Renders three canvas-side cues for ship movement in the galaxy view,
 * all gated through the same FoW pass that already controls in-flight
 * ship dots in `galaxy-view.ts`:
 *
 *  - **Path line** from origin to destination for mid-jump ships. Solid
 *    in the empire's colour for own / teammate fleets; dotted for
 *    visible enemy fleets. Alpha fades toward the destination end so
 *    the line doesn't clutter the destination's halo + indicator
 *    column.
 *  - **ETA badge** at the destination end of own / teammate path lines.
 *    Hidden for enemy fleets (intel asymmetry per `docs/MAP.md §4.3.2`).
 *  - **Charge-up source-system pulse** — pre-jump phase, before the
 *    path appears. Pulsing ring at the source system in the empire's
 *    colour. Same intel-asymmetry rules as paths.
 *
 * The companion fleet-composition hover tooltip (also locked in §4.3.2)
 * is intentionally deferred to v4M6.6 polish — hover detection on a
 * moving in-flight dot is a DOM-side concern best landed alongside the
 * other UX polish items rather than mid-render-pass.
 *
 * No save-format change. No state mutation.
 */

import { areTeammates } from '../game/teams';
import {
  EMPIRE_NEUTRAL_ID,
  type GameState,
  type ShipInstance,
  type SystemId,
} from '../state/types/index';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';

const TICK_HZ = 10;

/**
 * Screen position of an in-flight ship, interpolating linearly between
 * origin and target stars by `1 - jumpTicksRemaining / jumpDurationTotal`.
 * Returns `null` when the ship isn't mid-jump, when either endpoint
 * system is missing, or when `jumpDurationTotal <= 0`.
 *
 * Exported so the v4M6.6 fleet-composition hover hit-test in
 * `src/ui/input.ts` can share the same interpolation math the
 * `galaxy-view.ts` render path uses — keeps the visible dot and the
 * hit-test target locked together.
 */
export function inFlightShipScreenPos(
  state: GameState,
  ship: ShipInstance,
  camera: { centerX: number; centerY: number; zoom: number },
  vp: Viewport,
): { x: number; y: number } | null {
  if (
    ship.jumpOriginSystemId === null ||
    ship.jumpTargetSystemId === null ||
    ship.jumpDurationTotal <= 0
  ) {
    return null;
  }
  const origin = state.systems.get(ship.jumpOriginSystemId);
  const target = state.systems.get(ship.jumpTargetSystemId);
  if (!origin || !target) return null;
  const t = 1 - ship.jumpTicksRemaining / ship.jumpDurationTotal;
  const wx = origin.x + (target.x - origin.x) * t;
  const wy = origin.y + (target.y - origin.y) * t;
  return worldToScreen(camera, vp, { x: wx, y: wy });
}

/**
 * Format the ship's remaining jump duration as "ETA 4.2s". Falls back
 * to "—" defensively if the duration is non-positive (shouldn't
 * happen on a mid-jump ship but guards against future state-shape
 * drift). Returns the raw string; the caller positions it.
 */
export function formatEta(jumpTicksRemaining: number): string {
  if (jumpTicksRemaining <= 0) return '—';
  const seconds = jumpTicksRemaining / TICK_HZ;
  return seconds < 10 ? `ETA ${seconds.toFixed(1)}s` : `ETA ${Math.round(seconds)}s`;
}

/**
 * FoW classification — does the viewer empire see this ship at all,
 * and if so, with full detail or degraded?
 *
 *  - `'own'`: viewer-owned ship. Full detail (path + ETA).
 *  - `'teammate'`: ally's ship under team-vision union. Full detail.
 *  - `'enemy-visible'`: enemy ship currently tracked in teamVision.
 *    Degraded (dotted line, no ETA).
 *  - `'hidden'`: not drawn at all (no path, no pulse).
 *
 * Dev reveal-all (F10) collapses every non-own classification to
 * `'enemy-visible'` so the cheat surfaces the same render path the
 * player would otherwise see.
 */
export type FleetFoWClass = 'own' | 'teammate' | 'enemy-visible' | 'hidden';

export function classifyShipForViewer(
  state: GameState,
  ui: UiState,
  ship: ShipInstance,
): FleetFoWClass {
  const viewerId = viewerEmpireId(state, ui);
  if (ship.ownerEmpireId === viewerId) return 'own';
  if (ship.ownerEmpireId === EMPIRE_NEUTRAL_ID) return 'hidden';
  if (areTeammates(state, viewerId, ship.ownerEmpireId)) return 'teammate';
  if (ui.devRevealAll) return 'enemy-visible';
  const vision = state.teamVision.get(viewerId);
  const rec = vision?.get(ship.id);
  if (!rec) return 'hidden';
  // Ghost rows (seenVia=null) are stale memory — don't draw movement
  // cues against stale intel.
  if (rec.seenVia === null) return 'hidden';
  return 'enemy-visible';
}

/**
 * Draw a single path segment from `originScreen` to `destScreen`. Solid
 * for own/teammate; dashed for enemy-visible. Linear-gradient alpha
 * fade toward the destination so the line tapers cleanly into the
 * destination's halo + indicator column.
 */
export function drawJumpPath(
  ctx: CanvasRenderingContext2D,
  originScreen: { x: number; y: number },
  destScreen: { x: number; y: number },
  color: string,
  variant: 'solid' | 'dashed',
): void {
  ctx.save();
  // Two-stop gradient: full alpha at origin, fully transparent at dest.
  // Canvas linear gradients project the colour stops along the line so
  // a single hex colour with an alpha suffix on each stop reads as a
  // gentle fade.
  const grad = ctx.createLinearGradient(
    originScreen.x,
    originScreen.y,
    destScreen.x,
    destScreen.y,
  );
  grad.addColorStop(0, color + 'CC');
  grad.addColorStop(0.7, color + '99');
  grad.addColorStop(1, color + '00');
  ctx.strokeStyle = grad;
  ctx.lineWidth = variant === 'solid' ? 1.5 : 1.25;
  if (variant === 'dashed') ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.moveTo(originScreen.x, originScreen.y);
  ctx.lineTo(destScreen.x, destScreen.y);
  ctx.stroke();
  if (variant === 'dashed') ctx.setLineDash([]);
  ctx.restore();
}

/**
 * ETA badge at the destination end of a path line. Sits a few px
 * inward from the destination star along the path so it doesn't
 * collide with the destination's label (which sits above the star).
 * Caller has already gated for own/teammate.
 */
export function drawEtaBadge(
  ctx: CanvasRenderingContext2D,
  originScreen: { x: number; y: number },
  destScreen: { x: number; y: number },
  starRadius: number,
  color: string,
  jumpTicksRemaining: number,
): void {
  // Anchor point: walk back from the destination star by `starRadius
  // × 2.6 + 12 px` along the line direction, so the badge floats just
  // outside the star's per-class halo / per-system overlay ring.
  const dx = destScreen.x - originScreen.x;
  const dy = destScreen.y - originScreen.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return;
  const back = starRadius * 2.6 + 12;
  const ax = destScreen.x - (dx / len) * back;
  const ay = destScreen.y - (dy / len) * back;
  const text = formatEta(jumpTicksRemaining);
  ctx.save();
  ctx.font =
    '11px Inter, system-ui, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Pill background for readability against busy starfields.
  const padX = 5;
  const padY = 2;
  const w = ctx.measureText(text).width + padX * 2;
  const h = 14;
  ctx.fillStyle = 'rgba(10, 14, 24, 0.78)';
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  const rx = ax - w / 2;
  const ry = ay - h / 2;
  ctx.beginPath();
  // Rounded-corner rect via arcs for the pill shape.
  const r = 4;
  ctx.moveTo(rx + r, ry);
  ctx.lineTo(rx + w - r, ry);
  ctx.quadraticCurveTo(rx + w, ry, rx + w, ry + r);
  ctx.lineTo(rx + w, ry + h - r);
  ctx.quadraticCurveTo(rx + w, ry + h, rx + w - r, ry + h);
  ctx.lineTo(rx + r, ry + h);
  ctx.quadraticCurveTo(rx, ry + h, rx, ry + h - r);
  ctx.lineTo(rx, ry + r);
  ctx.quadraticCurveTo(rx, ry, rx + r, ry);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color;
  void padY;
  ctx.fillText(text, ax, ay);
  ctx.restore();
}

/**
 * Pulsing source-system ring during the pre-jump charge-up phase. The
 * fleet is at the source system but hasn't decided which Jump Area
 * entry point to use yet (per `HYPERSPACE.md §4`), so we don't draw a
 * path line. The pulse is the player-facing "something is happening
 * here" cue. Period ~0.5 s at 10 Hz (same cadence as the v4M6.1
 * selection-ring pulse).
 */
export function drawChargeUpRing(
  ctx: CanvasRenderingContext2D,
  sourceScreen: { x: number; y: number },
  starRadius: number,
  color: string,
  tick: number,
): void {
  const pulse = 1 + 0.12 * Math.sin(tick * 1.26);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.arc(sourceScreen.x, sourceScreen.y, (starRadius + 6) * pulse, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * Top-level driver. Iterates every ship once, classifies via
 * `classifyShipForViewer`, and dispatches to path / ETA / charge-up
 * draws as appropriate. Called once per frame from `galaxy-view.ts`
 * between the v4M6.3a picker pass and the in-flight ship dots so the
 * lines / ETA / pulse all sit behind the dots.
 *
 * Iteration order is `state.ships.values()` — Map preserves insertion
 * order so the draw order is deterministic across frames.
 */
export function drawGalaxyPaths(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  starRadius: number,
): void {
  for (const ship of state.ships.values()) {
    const fow = classifyShipForViewer(state, ui, ship);
    if (fow === 'hidden') continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const color = empire.color;

    // Mid-jump → path line + ETA (own/teammate).
    if (
      ship.jumpOriginSystemId !== null &&
      ship.jumpTargetSystemId !== null &&
      ship.jumpDurationTotal > 0
    ) {
      const origin = state.systems.get(ship.jumpOriginSystemId);
      const target = state.systems.get(ship.jumpTargetSystemId);
      if (!origin || !target) continue;
      const originScreen = worldToScreen(ui.galaxyCamera, vp, {
        x: origin.x,
        y: origin.y,
      });
      const destScreen = worldToScreen(ui.galaxyCamera, vp, {
        x: target.x,
        y: target.y,
      });
      drawJumpPath(
        ctx,
        originScreen,
        destScreen,
        color,
        fow === 'enemy-visible' ? 'dashed' : 'solid',
      );
      // ETA only for own/teammate per §4.3.2 intel asymmetry.
      if (fow === 'own' || fow === 'teammate') {
        drawEtaBadge(
          ctx,
          originScreen,
          destScreen,
          starRadius,
          color,
          ship.jumpTicksRemaining,
        );
      }
      continue;
    }

    // Charge-up → pulsing source-system ring (no path yet — the
    // fleet hasn't picked its Jump Area entry point per
    // `HYPERSPACE.md §4`). Source system = `ship.systemId` (the
    // ship is still on-map at the source during charge-up).
    if (ship.chargeUpTicksRemaining > 0 && ship.systemId !== null) {
      const source = state.systems.get(ship.systemId);
      if (!source) continue;
      const sourceScreen = worldToScreen(ui.galaxyCamera, vp, {
        x: source.x,
        y: source.y,
      });
      drawChargeUpRing(ctx, sourceScreen, starRadius, color, state.tick);
      continue;
    }
  }
}

/**
 * Cluster in-flight ships by (origin, destination, owner) so the
 * companion v4M6.6 hover-tooltip (deferred from this checkpoint) can
 * surface fleet composition without re-walking `state.ships` from
 * scratch. Exported here so the tooltip module — when it lands — can
 * reach in via a typed API rather than reproducing the iteration.
 *
 * Keying is `${ownerEmpireId}:${jumpOriginSystemId}:${jumpTargetSystemId}`.
 * Charge-up ships use `pendingJumpSystemId` as the destination so a
 * fleet that hasn't departed yet still groups by intent.
 */
export interface FleetGroup {
  ownerEmpireId: number;
  originSystemId: SystemId;
  destSystemId: SystemId;
  /** Sum of ships in the group. */
  count: number;
  /** Per-chassis counts; iterates in chassis-string ascending order for determinism. */
  chassisCounts: Map<string, number>;
}

export function aggregateInFlightFleet(state: GameState): Map<string, FleetGroup> {
  const out = new Map<string, FleetGroup>();
  for (const ship of state.ships.values()) {
    let originId: SystemId | null = null;
    let destId: SystemId | null = null;
    if (
      ship.jumpOriginSystemId !== null &&
      ship.jumpTargetSystemId !== null &&
      ship.jumpDurationTotal > 0
    ) {
      originId = ship.jumpOriginSystemId;
      destId = ship.jumpTargetSystemId;
    } else if (
      ship.chargeUpTicksRemaining > 0 &&
      ship.systemId !== null &&
      ship.pendingJumpSystemId !== null
    ) {
      originId = ship.systemId;
      destId = ship.pendingJumpSystemId;
    } else {
      continue;
    }
    const key = `${ship.ownerEmpireId}:${originId}:${destId}`;
    let group = out.get(key);
    if (!group) {
      group = {
        ownerEmpireId: ship.ownerEmpireId,
        originSystemId: originId,
        destSystemId: destId,
        count: 0,
        chassisCounts: new Map(),
      };
      out.set(key, group);
    }
    group.count += 1;
    group.chassisCounts.set(
      ship.chassis,
      (group.chassisCounts.get(ship.chassis) ?? 0) + 1,
    );
  }
  return out;
}
